import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Observable, forkJoin } from 'rxjs';
import { IFaqDto, IFaqGroupDto, IFaqGroupItemDto } from '../../interfaces/Faq/ifaq-dto';
import { FaqService } from '../../services/faq.service';
import { SharedService } from '../../services/shared.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

/** Captures group/item order before a drag so we can revert on Discard. */
interface IGroupItemOrderSnapshot {
  groupIdsOrdered: number[];
  itemsOrderedByGroupId: { [groupId: number]: number[] };
  groupDisplayOrderById: { [groupId: number]: number };
  itemDisplayOrderById: { [itemId: number]: number };
}

/** Captures faq order before a drag so we can revert on Discard. */
interface IFaqListOrderSnapshot {
  faqIdsOrdered: number[];
  faqDisplayOrderById: { [faqId: number]: number };
}

@Component({
  selector: 'app-faq-management',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.css']
})
export class FaqManagementComponent implements OnInit {
  faqs: IFaqDto[] = [];
  loading = false;
  isEditing = false;
  timestamp = new Date().getTime();
  safeYouTubeUrl: SafeResourceUrl | null = null;
  
  expandedFaqId: number | null = null;
  
  currentFaq: Partial<IFaqDto> = {};
  currentGroup: Partial<IFaqGroupDto> = {};
  currentItem: Partial<IFaqGroupItemDto> = {};

  showLoadingMessage: boolean;
  showFileManager = false;
  private originalFaqOrderById: { [id: number]: number } = {};
  private originalGroupOrderById: { [id: number]: number } = {};
  private originalItemOrderById: { [id: number]: number } = {};
  private groupItemOrderSnapshotBeforeDrag: IGroupItemOrderSnapshot | null = null;
  private faqListOrderSnapshotBeforeDrag: IFaqListOrderSnapshot | null = null;

  /** FAQ id -> expanded group id (in-memory only for this screen). */
  expandedGroupIdByFaqId: { [faqId: number]: number } = {};

  constructor(private faqService: FaqService, private sharedService: SharedService, private sanitizer: DomSanitizer) { }

  ngOnInit() {
    this.sharedService.reloadUserPrivileges();
    this.expandedFaqId = null;
    this.expandedGroupIdByFaqId = {};
    this.loadFaqs();
  }

  toggleFileManager() {
    this.showFileManager = !this.showFileManager;
  }


  loadFaqs() {
    this.loading = true;
    this.faqService.getFaqs().subscribe(data => {
      this.faqs = data;
      this.loading = false;
      this.showLoadingMessage = false;
      this.captureOriginalOrders();
      this.pruneExpandedGroupState();
    }, err => {
      this.loading = false;
      this.showLoadingMessage = false;
    });
  }

  isFaqPanelExpanded(faq: IFaqDto): boolean {
    return this.expandedFaqId === faq.id;
  }

  onFaqPanelExpandedChange(faq: IFaqDto, opened: boolean): void {
    if (opened) {
      this.expandedFaqId = faq.id;
    } else {
      if (this.expandedFaqId === faq.id) this.expandedFaqId = null;
    }
  }

  isGroupPanelExpanded(faqId: number, groupId: number): boolean {
    return this.expandedGroupIdByFaqId[faqId] === groupId;
  }

  onGroupPanelExpandedChange(faqId: number, groupId: number, opened: boolean): void {
    if (opened) {
      this.expandedGroupIdByFaqId[faqId] = groupId;
    } else {
      if (this.expandedGroupIdByFaqId[faqId] === groupId) {
        delete this.expandedGroupIdByFaqId[faqId];
      }
    }
  }

  private pruneExpandedGroupState(): void {
    const validFaqIds: { [id: number]: boolean } = {};
    for (let i = 0; i < this.faqs.length; i++) {
      validFaqIds[this.faqs[i].id] = true;
    }

    const keys = Object.keys(this.expandedGroupIdByFaqId);
    for (let k = 0; k < keys.length; k++) {
      const faqId = parseInt(keys[k], 10);
      if (isNaN(faqId) || !validFaqIds[faqId]) {
        delete this.expandedGroupIdByFaqId[faqId];
      }
    }

    for (let f = 0; f < this.faqs.length; f++) {
      const faq = this.faqs[f];
      const gid = this.expandedGroupIdByFaqId[faq.id];
      if (gid == null) {
        continue;
      }
      let exists = false;
      if (faq.faqGroups && faq.faqGroups.length > 0) {
        for (let g = 0; g < faq.faqGroups.length; g++) {
          if (faq.faqGroups[g].id === gid) {
            exists = true;
            break;
          }
        }
      }
      if (!exists) {
        delete this.expandedGroupIdByFaqId[faq.id];
      }
    }
  }

  // FAQ Operations
  onCreateFaq() {
    this.isEditing = false;
    let nextOrder = 1;
    if (this.faqs && this.faqs.length > 0) {
      nextOrder = Math.max(...this.faqs.map(f => f.displayOrder)) + 1;
    }
    this.currentFaq = { id: 0, isActive: true, displayOrder: nextOrder };
    this.openModal('faqModal');
  }

  onEditFaq(faq: IFaqDto) {
    this.isEditing = true;
    this.currentFaq = { ...faq };
    this.openModal('faqModal');
  }


  saveFaq() {
    if (this.currentFaq.title) this.currentFaq.title = this.currentFaq.title.trim();
    if (this.currentFaq.code) this.currentFaq.code = this.currentFaq.code.trim();

    this.faqService.saveFaq(this.currentFaq as IFaqDto).subscribe(() => {
      this.closeModal('faqModal');
      this.loadFaqs();
      this.sharedService.showSuccessMessage = true;
    });
  }

  // Group Operations
  onCreateGroup(faqId: number) {
    this.isEditing = false;
    let nextOrder = 1;
    const faq = this.faqs.find(f => f.id === faqId);
    if (faq && faq.faqGroups && faq.faqGroups.length > 0) {
      nextOrder = Math.max(...faq.faqGroups.map(g => g.displayOrder)) + 1;
    }
    this.currentGroup = { id: 0, faqId: faqId, isActive: true, displayOrder: nextOrder };
    this.openModal('groupModal');
  }

  onEditGroup(group: IFaqGroupDto) {
    this.isEditing = true;
    this.currentGroup = { ...group };
    this.openModal('groupModal');
  }


  saveGroup() {
    if (this.currentGroup.title) this.currentGroup.title = this.currentGroup.title.trim();

    this.faqService.saveFaqGroup(this.currentGroup as IFaqGroupDto).subscribe(() => {
      this.closeModal('groupModal');
      this.loadFaqs();
      this.sharedService.showSuccessMessage = true;
    });
  }

  // Item Operations
  onCreateItem(group: IFaqGroupDto) {
    this.isEditing = false;
    let nextOrder = 1;
    if (group && group.faqGroupItems && group.faqGroupItems.length > 0) {
      nextOrder = Math.max(...group.faqGroupItems.map(i => i.displayOrder)) + 1;
    }
    this.currentItem = { id: 0, faqGroupId: group.id, isActive: true, displayOrder: nextOrder, showSeparator: false };
    this.openModal('itemModal');
  }

  onEditItem(item: IFaqGroupItemDto) {
    this.isEditing = true;
    this.currentItem = { ...item };
    this.updateSafeYouTubeUrl();
    this.openModal('itemModal');
  }

  updateSafeYouTubeUrl() {
    if (this.currentItem.videoLink && this.isYouTubeLink(this.currentItem.videoLink)) {
      this.safeYouTubeUrl = this.getYouTubeEmbedUrl(this.currentItem.videoLink);
    } else {
      this.safeYouTubeUrl = null;
    }
  }

  isYouTubeLink(url: string): boolean {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  }

  private getYouTubeEmbedUrl(url: string): SafeResourceUrl {
    let videoId = '';
    try {
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1].split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('embed/')[1].split('?')[0];
      }
    } catch (e) {
      console.error('Error parsing YouTube URL:', url, e);
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${videoId}`);
  }


  saveItem() {
    if (this.currentItem.title) this.currentItem.title = this.currentItem.title.trim();
    if (this.currentItem.previewLink) this.currentItem.previewLink = this.currentItem.previewLink.trim();
    if (this.currentItem.documentLink) this.currentItem.documentLink = this.currentItem.documentLink.trim();
    if (this.currentItem.buttonTitle) this.currentItem.buttonTitle = this.currentItem.buttonTitle.trim();
    if (this.currentItem.buttonDesc) this.currentItem.buttonDesc = this.currentItem.buttonDesc.trim();
    if (this.currentItem.videoLink) this.currentItem.videoLink = this.currentItem.videoLink.trim();
    if (this.currentItem.videoDesc) this.currentItem.videoDesc = this.currentItem.videoDesc.trim();
    if (this.currentItem.videoTitle) this.currentItem.videoTitle = this.currentItem.videoTitle.trim();

    this.faqService.saveFaqGroupItem(this.currentItem as IFaqGroupItemDto).subscribe(() => {
      this.closeModal('itemModal');
      this.loadFaqs();
      this.sharedService.showSuccessMessage = true;
    });
  }

  // Modal helpers
  openModal(modalId: string) {
    const modalElement = document.getElementById(modalId);
    const modal = new bootstrap.Modal(modalElement);
    modal.show();
  }

  closeModal(modalId: string) {
    const modalElement = document.getElementById(modalId);
    const modal = bootstrap.Modal.getInstance(modalElement);
    if (modal) {
      modal.hide();
    }
  }

  /**
   * Call when the user grabs a group or item handle (before reorder is applied).
   */
  onReorderDragStarted(faqId: number): void {
    this.captureGroupItemOrderSnapshotBeforeDrag(faqId);
  }

  onFaqReorderDragStarted(): void {
    this.captureFaqListOrderSnapshotBeforeDrag();
  }

  dropFaq(event: CdkDragDrop<IFaqDto[]>) {
    if (!this.faqs || this.faqs.length === 0) {
      this.faqListOrderSnapshotBeforeDrag = null;
      return;
    }
    if (event.previousIndex === event.currentIndex) {
      this.faqListOrderSnapshotBeforeDrag = null;
      return;
    }

    moveItemInArray(this.faqs, event.previousIndex, event.currentIndex);
    this.updateDisplayOrders(this.faqs);
    const snap = this.faqListOrderSnapshotBeforeDrag;
    this.promptSaveAfterFaqReorder(snap);
  }

  dropGroup(faq: IFaqDto, event: CdkDragDrop<IFaqGroupDto[]>) {
    if (!faq || !faq.faqGroups) {
      this.groupItemOrderSnapshotBeforeDrag = null;
      return;
    }
    if (event.previousIndex === event.currentIndex) {
      this.groupItemOrderSnapshotBeforeDrag = null;
      return;
    }

    moveItemInArray(faq.faqGroups, event.previousIndex, event.currentIndex);
    this.updateDisplayOrders(faq.faqGroups);
    const snap = this.groupItemOrderSnapshotBeforeDrag;
    this.promptSaveAfterGroupItemReorder(faq.id, snap);
  }

  dropItem(faqId: number, group: IFaqGroupDto, event: CdkDragDrop<IFaqGroupItemDto[]>) {
    if (!group || !group.faqGroupItems) {
      this.groupItemOrderSnapshotBeforeDrag = null;
      return;
    }
    if (event.previousIndex === event.currentIndex) {
      this.groupItemOrderSnapshotBeforeDrag = null;
      return;
    }

    moveItemInArray(group.faqGroupItems, event.previousIndex, event.currentIndex);
    this.updateDisplayOrders(group.faqGroupItems);
    const snap = this.groupItemOrderSnapshotBeforeDrag;
    this.promptSaveAfterGroupItemReorder(faqId, snap);
  }

  private updateDisplayOrders(arr: any[]) {
    arr.forEach((item, index) => {
      item.displayOrder = index + 1;
    });
  }

  private promptSaveAfterFaqReorder(snap: IFaqListOrderSnapshot | null): void {
    if (!snap) return;

    Swal.fire({
      title: 'Save FAQ order?',
      text: 'Save the new FAQ order? Choose Discard to revert this drag.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Save',
      cancelButtonText: 'Discard',
      reverseButtons: true
    }).then(result => {
      this.faqListOrderSnapshotBeforeDrag = null;
      if (result.isConfirmed) {
        this.persistChangedFaqOrders(snap);
      } else {
        this.restoreFaqListOrderFromSnapshot(snap);
      }
    });
  }

  private promptSaveAfterGroupItemReorder(faqId: number, snap: IGroupItemOrderSnapshot | null): void {
    if (!snap) {
      return;
    }

    Swal.fire({
      title: 'Save order?',
      text: 'Save the new order to the server? Choose Discard to revert this drag.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Save',
      cancelButtonText: 'Discard',
      reverseButtons: true
    }).then(result => {
      this.groupItemOrderSnapshotBeforeDrag = null;
      if (result.isConfirmed) {
        this.persistChangedGroupItemOrders(faqId, snap);
      } else {
        this.restoreGroupItemOrderFromSnapshot(faqId, snap);
      }
    });
  }

  private persistChangedFaqOrders(snapForRevert: IFaqListOrderSnapshot): void {
    const changedFaqs = this.faqs.filter(f => this.originalFaqOrderById[f.id] !== f.displayOrder);
    if (changedFaqs.length === 0) {
      this.sharedService.showTempSuccessMessage();
      return;
    }

    this.loading = true;
    const saveRequests = changedFaqs.map(f => this.faqService.saveFaq(f));
    forkJoin(saveRequests).subscribe(() => {
      this.finishOrderSave();
    }, err => {
      this.loading = false;
      this.restoreFaqListOrderFromSnapshot(snapForRevert);
      Swal.fire({
        icon: 'error',
        title: 'Save failed',
        text: err && err.message ? err.message : 'Could not save FAQ order. The list was restored.'
      });
    });
  }

  private persistChangedGroupItemOrders(faqId: number, snapForRevert: IGroupItemOrderSnapshot): void {
    const faq = this.faqs.find(f => f.id === faqId);
    if (!faq) return;

    var changedGroups = faq.faqGroups.filter(g =>
      this.originalGroupOrderById[g.id] !== g.displayOrder
    );
    var changedItems: IFaqGroupItemDto[] = [];
    faq.faqGroups.forEach(g => {
      if (!g.faqGroupItems || g.faqGroupItems.length === 0) return;
      var groupChangedItems = g.faqGroupItems.filter(i =>
        this.originalItemOrderById[i.id] !== i.displayOrder
      );
      changedItems.push.apply(changedItems, groupChangedItems);
    });

    if (changedGroups.length === 0 && changedItems.length === 0) {
      this.sharedService.showTempSuccessMessage();
      return;
    }

    this.loading = true;
    var saveRequests: Observable<boolean>[] = [];
    if (changedGroups.length > 0) {
      saveRequests.push(this.faqService.saveFaqGroups(changedGroups));
    }
    if (changedItems.length > 0) {
      saveRequests.push(this.faqService.saveFaqGroupItems(changedItems));
    }

    forkJoin(saveRequests).subscribe(() => {
      this.finishOrderSave();
    }, err => {
      this.loading = false;
      this.restoreGroupItemOrderFromSnapshot(faqId, snapForRevert);
      Swal.fire({
        icon: 'error',
        title: 'Save failed',
        text: err && err.message ? err.message : 'Could not save the new order. The list was restored.'
      });
    });
  }

  private captureFaqListOrderSnapshotBeforeDrag(): void {
    if (!this.faqs || this.faqs.length === 0) {
      this.faqListOrderSnapshotBeforeDrag = null;
      return;
    }

    var faqIdsOrdered: number[] = [];
    var faqDisplayOrderById: { [faqId: number]: number } = {};
    for (let i = 0; i < this.faqs.length; i++) {
      faqIdsOrdered.push(this.faqs[i].id);
      faqDisplayOrderById[this.faqs[i].id] = this.faqs[i].displayOrder;
    }
    this.faqListOrderSnapshotBeforeDrag = { faqIdsOrdered, faqDisplayOrderById };
  }

  private captureGroupItemOrderSnapshotBeforeDrag(faqId: number): void {
    const faq = this.faqs.find(f => f.id === faqId);
    if (!faq || !faq.faqGroups) {
      this.groupItemOrderSnapshotBeforeDrag = null;
      return;
    }

    var groupIdsOrdered: number[] = [];
    var itemsOrderedByGroupId: { [groupId: number]: number[] } = {};
    var groupDisplayOrderById: { [groupId: number]: number } = {};
    var itemDisplayOrderById: { [itemId: number]: number } = {};

    for (let gi = 0; gi < faq.faqGroups.length; gi++) {
      const g = faq.faqGroups[gi];
      groupIdsOrdered.push(g.id);
      groupDisplayOrderById[g.id] = g.displayOrder;
      itemsOrderedByGroupId[g.id] = [];
      if (!g.faqGroupItems || g.faqGroupItems.length === 0) {
        continue;
      }
      for (let ii = 0; ii < g.faqGroupItems.length; ii++) {
        const it = g.faqGroupItems[ii];
        itemsOrderedByGroupId[g.id].push(it.id);
        itemDisplayOrderById[it.id] = it.displayOrder;
      }
    }

    this.groupItemOrderSnapshotBeforeDrag = {
      groupIdsOrdered,
      itemsOrderedByGroupId,
      groupDisplayOrderById,
      itemDisplayOrderById
    };
  }

  private restoreFaqListOrderFromSnapshot(snap: IFaqListOrderSnapshot): void {
    if (!this.faqs || this.faqs.length === 0) return;
    var faqById: { [id: number]: IFaqDto } = {};
    for (let i = 0; i < this.faqs.length; i++) faqById[this.faqs[i].id] = this.faqs[i];

    var reordered: IFaqDto[] = [];
    for (let j = 0; j < snap.faqIdsOrdered.length; j++) {
      const f = faqById[snap.faqIdsOrdered[j]];
      if (f) reordered.push(f);
    }

    this.faqs.length = 0;
    for (let k = 0; k < reordered.length; k++) this.faqs.push(reordered[k]);
    for (let m = 0; m < this.faqs.length; m++) {
      this.faqs[m].displayOrder = snap.faqDisplayOrderById[this.faqs[m].id];
    }
  }

  private restoreGroupItemOrderFromSnapshot(faqId: number, snap: IGroupItemOrderSnapshot): void {
    const faq = this.faqs.find(f => f.id === faqId);
    if (!faq || !faq.faqGroups) {
      return;
    }

    var groupById: { [id: number]: IFaqGroupDto } = {};
    for (let i = 0; i < faq.faqGroups.length; i++) {
      const g = faq.faqGroups[i];
      groupById[g.id] = g;
    }

    var newGroups: IFaqGroupDto[] = [];
    for (let gi = 0; gi < snap.groupIdsOrdered.length; gi++) {
      const id = snap.groupIdsOrdered[gi];
      const g = groupById[id];
      if (g) {
        newGroups.push(g);
      }
    }

    faq.faqGroups.length = 0;
    for (let j = 0; j < newGroups.length; j++) {
      faq.faqGroups.push(newGroups[j]);
    }

    for (let k = 0; k < faq.faqGroups.length; k++) {
      const g = faq.faqGroups[k];
      g.displayOrder = snap.groupDisplayOrderById[g.id];
      const itemIds = snap.itemsOrderedByGroupId[g.id];
      if (!itemIds || !g.faqGroupItems) {
        continue;
      }

      var itemById: { [id: number]: IFaqGroupItemDto } = {};
      for (let m = 0; m < g.faqGroupItems.length; m++) {
        const it = g.faqGroupItems[m];
        itemById[it.id] = it;
      }

      var newItems: IFaqGroupItemDto[] = [];
      for (let n = 0; n < itemIds.length; n++) {
        const restored = itemById[itemIds[n]];
        if (restored) {
          newItems.push(restored);
        }
      }

      g.faqGroupItems.length = 0;
      for (let p = 0; p < newItems.length; p++) {
        g.faqGroupItems.push(newItems[p]);
      }

      if (g.faqGroupItems) {
        for (let q = 0; q < g.faqGroupItems.length; q++) {
          const item = g.faqGroupItems[q];
          item.displayOrder = snap.itemDisplayOrderById[item.id];
        }
      }
    }
  }

  private finishOrderSave() {
    this.loading = false;
    this.sharedService.showTempSuccessMessage();
    this.loadFaqs();
  }

  private captureOriginalOrders() {
    this.originalFaqOrderById = {};
    this.originalGroupOrderById = {};
    this.originalItemOrderById = {};

    this.faqs.forEach(faq => {
      this.originalFaqOrderById[faq.id] = faq.displayOrder;
      if (!faq.faqGroups || faq.faqGroups.length === 0) return;
      faq.faqGroups.forEach(group => {
        this.originalGroupOrderById[group.id] = group.displayOrder;
        if (!group.faqGroupItems || group.faqGroupItems.length === 0) return;
        group.faqGroupItems.forEach(item => {
          this.originalItemOrderById[item.id] = item.displayOrder;
        });
      });
    });
  }
}
